/**
 * WEBSOCKET CONTEXT
 * 
 * Manages WebSocket connection for real-time updates
 * 
 * Features:
 * - Auto-connect when user is authenticated
 * - Auto-reconnect on disconnect
 * - Subscribe/unsubscribe to groups
 * - Connection status tracking
 * 
 * Usage:
 * const { socket, isConnected, subscribeToGroup } = useWebSocket();
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { logger } from '../utils/logger';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  subscribeToGroup: (groupId: string) => void;
  unsubscribeFromGroup: (groupId: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token');
    if (!token) {
      logger.debug('No auth token, skipping WebSocket connection');
      return;
    }

    // Determine backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 
                      (window.location.hostname === 'localhost' 
                        ? 'http://localhost:3001' 
                        : 'https://settler-production.up.railway.app');

    logger.info(`🔌 Connecting to WebSocket: ${backendUrl}`);

    // Create WebSocket connection
    const socketInstance = io(backendUrl, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
    });

    // Connection events
    socketInstance.on('connect', () => {
      logger.info('✅ WebSocket connected');
      setIsConnected(true);
    });

    socketInstance.on('disconnect', (reason) => {
      logger.warn(`🔌 WebSocket disconnected: ${reason}`);
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      logger.error('❌ WebSocket connection error:', error);
      setIsConnected(false);
    });

    socketInstance.on('reconnect', (attemptNumber) => {
      logger.info(`🔄 WebSocket reconnected after ${attemptNumber} attempts`);
      setIsConnected(true);
    });

    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      logger.debug(`🔄 Reconnection attempt ${attemptNumber}...`);
    });

    socketInstance.on('reconnect_error', (error) => {
      logger.error('❌ Reconnection failed:', error);
    });

    socketInstance.on('reconnect_failed', () => {
      logger.error('❌ Reconnection failed after all attempts');
      setIsConnected(false);
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      logger.info('🔌 Cleaning up WebSocket connection');
      socketInstance.disconnect();
    };
  }, []); // Empty dependency array - only run once

  /**
   * Subscribe to group updates
   */
  const subscribeToGroup = useCallback((groupId: string) => {
    if (socket && socket.connected) {
      socket.emit('subscribe:group', groupId);
      logger.debug(`📢 Subscribed to group ${groupId}`);
    } else {
      logger.warn(`Cannot subscribe to group ${groupId} - socket not connected`);
    }
  }, [socket]);

  /**
   * Unsubscribe from group updates
   */
  const unsubscribeFromGroup = useCallback((groupId: string) => {
    if (socket && socket.connected) {
      socket.emit('unsubscribe:group', groupId);
      logger.debug(`🔇 Unsubscribed from group ${groupId}`);
    }
  }, [socket]);

  const value: WebSocketContextType = {
    socket,
    isConnected,
    subscribeToGroup,
    unsubscribeFromGroup,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to use WebSocket context
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

